<script lang="ts">
	import Gate from '#lib/Gate.svelte';
	import { t } from '#lib/i18n/tranlsator.svelte.ts';
	import { tooltip } from '@astlai/common/tooltip/tooltip.svelte.ts';
	import { getChatTimeStamp } from '#lib/util/time';
	import { getChatContext } from '#src/contexts/chat.context.svelte.ts';
	import { getModalContext } from '@astlai/common/contexts/modal.context.svelte.ts';
	import type { ChatMessage } from '#src/types/chat.ts';
	import IconCheck from 'virtual:icons/material-symbols/check-rounded';
	import CopyIcon from 'virtual:icons/material-symbols/content-copy-outline-rounded';
	import InfoIcon from 'virtual:icons/material-symbols/info-outline';
	import MarkdownIcon from 'virtual:icons/material-symbols/markdown-outline-rounded';
	import RetryIcon from 'virtual:icons/material-symbols/repeat-rounded';
	import MessageInfoDialog from './dialogs/MessageInfoDialog.svelte';

	const { message, isLast }: { message: ChatMessage; isLast: boolean } = $props();

	const chatCtx = getChatContext();
	const modalCtx = getModalContext();

	let copiedMessageId: string | null = $state(null);
	let copiedType: 'text' | 'md' | null = $state(null);

	function stripCitationTags(content: string): string {
		return content
			.replace(/<citation[^>]*>[\s\S]*?<\/citation>/gi, '')
			.replace(/<citation[^>]*\/>/gi, '');
	}

	function stripMarkdown(md: string): string {
		return md
			.replace(/```[\s\S]*?```/g, (m) => m.slice(m.indexOf('\n') + 1, m.lastIndexOf('```')).trim())
			.replace(/^#{1,6}\s+(.+)$/gm, '$1')
			.replace(/\*\*(.+?)\*\*/g, '$1')
			.replace(/\*(.+?)\*/g, '$1')
			.replace(/~~(.+?)~~/g, '$1')
			.replace(/`(.+?)`/g, '$1')
			.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
			.replace(/^[-*+]\s+/gm, '')
			.replace(/^\d+\.\s+/gm, '')
			.replace(/^>\s*/gm, '')
			.replace(/^-{3,}$/gm, '')
			.trim();
	}

	function citationsToMarkdownLinks(content: string): string {
		const seenSources = new Map<number, { title: string; url?: string }>();

		function replaceCitationTag(tag: string): string {
			const hashMatch = tag.match(/\bhash="([^"]+)"/i);
			if (!hashMatch) {
				return '';
			}
			const info = chatCtx.getCitationInfo(hashMatch[1]);
			if (!info) {
				return '';
			}
			const { source } = info;
			const url = source.url ?? source.secondary_source?.url;
			seenSources.set(source.citation_index, { title: source.title, url });
			return `[${source.citation_index}]`;
		}

		const withMarkers = content
			.replace(/<citation[^>]*>[\s\S]*?<\/citation>/gi, replaceCitationTag)
			.replace(/<citation[^>]*\/>/gi, replaceCitationTag);

		if (seenSources.size === 0) {
			return withMarkers;
		}

		const refList = [...seenSources.entries()]
			.sort(([a], [b]) => a - b)
			.map(([idx, { title, url }]) => (url ? `[${idx}]: [${title}](${url})` : `[${idx}]: ${title}`))
			.join('\n');

		return withMarkers.trimEnd() + '\n\n---\n\n' + refList;
	}

	async function copyPlainText() {
		if (!message.content) {
			return;
		}
		await navigator.clipboard.writeText(stripMarkdown(stripCitationTags(message.content)));
		copiedMessageId = message.id;
		copiedType = 'text';
		setTimeout(() => {
			copiedMessageId = null;
			copiedType = null;
		}, 2000);
	}

	async function copyMarkdown() {
		if (!message.content) {
			return;
		}
		await navigator.clipboard.writeText(citationsToMarkdownLinks(message.content));
		copiedMessageId = message.id;
		copiedType = 'md';
		setTimeout(() => {
			copiedMessageId = null;
			copiedType = null;
		}, 2000);
	}

	function openMessageInfoDialog() {
		modalCtx.openModal(MessageInfoDialog, { message });
	}
</script>

<div class="message-actions">
	{#if message.created}
		<span class="message-timestamp">{getChatTimeStamp(message.created)}</span>
	{/if}
	<div class="buttons">
		{#if message.content}
			<button
				class="icon-button"
				use:tooltip
				aria-label={t('chat.copyPlainText')}
				onclick={copyPlainText}
			>
				{#if copiedMessageId === message.id && copiedType === 'text'}
					<IconCheck />
				{:else}
					<CopyIcon />
				{/if}
			</button>
			<button
				class="icon-button"
				use:tooltip
				aria-label={t('chat.copyMarkdown')}
				onclick={copyMarkdown}
			>
				{#if copiedMessageId === message.id && copiedType === 'md'}
					<IconCheck />
				{:else}
					<MarkdownIcon />
				{/if}
			</button>
		{/if}
		{#if message.role === 'assistant'}
			{#if isLast}
				<button
					class="icon-button"
					use:tooltip
					aria-label={t('chat.retry')}
					onclick={() => chatCtx.retry()}><RetryIcon /></button
				>
			{/if}
			<Gate minimumRole="admin">
				<button
					class="icon-button"
					use:tooltip
					aria-label={t('chat.debugInfo')}
					onclick={openMessageInfoDialog}><InfoIcon /></button
				>
			</Gate>
		{/if}
	</div>
</div>

<style lang="scss">
	@use '@astlai/common/styles/mixins' as lib;

	.message-actions {
		opacity: 0;
		transition: opacity 200ms ease-out;
		@include lib.flex-row();
		gap: 1rem;
		@include lib.flex-align(flex-start, center);
		button {
			color: var(--text-secondary);
		}
		.message-timestamp {
			justify-self: flex-start;
			color: var(--text-tertiary);
			font-size: 0.75rem;
		}
		.buttons {
			@include lib.flex-row();
			gap: 0.5rem;
		}
	}
</style>
